/* global google */
/* global L */
import PropTypes from 'prop-types';

import React from 'react';
import ReactResizeDetector from 'react-resize-detector';
import _ from 'underscore';

import {logger, isMobile} from 'common/utils';

import {MapTypeRules} from './MapTypes';
import styles from './Map.css';


const MAP_OPTIONS = [
  'clickableIcons',
  'disableDoubleClickZoom',
  'disablePanMomentum',
  'draggable',
  'fullscreenControl',
  'mapTypeControl',
  'maxZoom',
  'minZoom',
  'scrollwheel',
  'streetViewControl',
  'styles',
  'zoomControl',
  'zoomControlOptions',
];

class Map extends React.Component {
  static displayName = 'Map';

  static propTypes = {
    bounds: PropTypes.shape({
      ne: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      sw: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
    }),
    center: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    children: PropTypes.node,
    clickableIcons: PropTypes.bool,
    disableDoubleClickZoom: PropTypes.bool,
    disablePanMomentum: PropTypes.bool,
    draggable: PropTypes.bool,
    forceUpdate: PropTypes.bool,
    fullscreenControl: PropTypes.bool,
    mapTypeControl: PropTypes.bool,
    mapTypes: PropTypes.arrayOf(PropTypes.string),
    maxZoom: PropTypes.number,
    minZoom: PropTypes.number,
    onBoundsChanged: PropTypes.func,
    onCenterChanged: PropTypes.func,
    onClick: PropTypes.func,
    onDragStart: PropTypes.func,
    onDragend: PropTypes.func,
    onResize: PropTypes.func,
    onZoomChanged: PropTypes.func,
    padding: PropTypes.object,
    renderer: PropTypes.string,
    scrollwheel: PropTypes.bool,
    streetViewControl: PropTypes.bool,
    styles: PropTypes.array,
    zoom: PropTypes.number,
    zoomControl: PropTypes.bool,
    zoomControlOptions: PropTypes.object,
  };

  static childContextTypes = {
    map: PropTypes.object,
    renderer: PropTypes.string,
  };

  static defaultProps = {
    clickableIcons: false,
    disableDoubleClickZoom: false,
    disablePanMomentum: true,
    draggable: true,
    fullscreenControl: true,
    mapTypeControl: true,
    scrollwheel: true,
    streetViewControl: true,
    zoomControl: !isMobile(),
    zoomControlOptions: {},
  };

  state = {
    map: null,
    mapInitialized: false,
  };

  getChildContext() {
    return {
      map: this.state.map,
      renderer: this.props.renderer,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.state.map) {
      if (!_.isEmpty(this.props.bounds)) {
        if (!_.isEqual(this.props.bounds, prevProps.bounds) ||
          !this.state.mapInitialized || this.props.forceUpdate) {
          this._updateBounds(this.state.map, this.props.bounds);
        }
      } else {
        if (!_.isEmpty(prevProps.bounds) ||
          !_.isEqual(this.props.center, prevProps.center) ||
          !this.state.mapInitialized || this.props.forceUpdate) {
          this._updateCenter(this.state.map, this.props.center);
        }
        if (!_.isEmpty(prevProps.bounds) ||
          !_.isEqual(this.props.zoom, prevProps.zoom) ||
          !this.state.mapInitialized || this.props.forceUpdate) {
          this._updateZoom(this.state.map, this.props.zoom);
        }
      }

      if (!this.state.mapInitialized) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({mapInitialized: true});
      }

      const oldOptions = this._getOptions(prevProps);
      const newOptions = this._getOptions(this.props);
      if (!_.isEqual(oldOptions, newOptions)) {
        this._updateOptions(this.state.map, newOptions);
      }
    }
  }

  componentWillUnmount() {
    if (this.state.map) {
      google.maps.event.clearInstanceListeners(this.state.map);
    }
  }

  _getOptions = (props) => {
    return {
      mapTypeControlOptions: {
        mapTypeIds: props.mapTypes,
      },
      ..._.pick(props, MAP_OPTIONS),
    };
  };

  _onResize = () => {
    if (this.props.onResize) this.props.onResize();
    google.maps.event.trigger(this.state.map, 'resize');
    if (this.props.center) this._updateCenter(this.state.map, this.props.center);
  };

  _updateCenter = (map, center) => {
    if (map && center) {
      map.panTo(center);
    }
  };

  _updateZoom = (map, zoom) => {
    if (map && zoom) {
      map.setZoom(zoom);
    }
  };

  _updateBounds = (map, bounds) => {
    if (map && bounds) {
      const googleBounds = new google.maps.LatLngBounds(bounds.sw, bounds.ne);
      if (this.props.padding) {
        map.fitBounds(googleBounds, this.props.padding);
      } else {
        map.fitBounds(googleBounds);
      }
    }
  };

  _updateOptions = (map, options) => {
    if (map && options) {
      map.setOptions(options);
    }
  };

  _updateBoundsChangedEvent = (map, boundsChanged) => {
    if (map && typeof boundsChanged === 'function') {
      google.maps.event.addListener(map, 'bounds_changed', () => {
        const bounds = map.getBounds();
        if (bounds) {
          boundsChanged({
            ne: {
              lat: bounds.getNorthEast().lat(),
              lng: bounds.getNorthEast().lng(),
            },
            sw: {
              lat: bounds.getSouthWest().lat(),
              lng: bounds.getSouthWest().lng(),
            },
          });
        }
      });
    }
  };

  _updateCenterChangedEvent = (map, centerChanged) => {
    if (map && typeof centerChanged === 'function') {
      google.maps.event.addListener(map, 'center_changed', () => {
        const center = map.getCenter();
        centerChanged({
          lat: center.lat(),
          lng: center.lng(),
        });
      });
    }
  };

  _updateClickEvent = (map, click) => {
    if (map && click && typeof click === 'function') {
      google.maps.event.addListener(map, 'click', (event) => {
        click({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      });
    }
  };

  _updateDragStartEvent = (map, dragstart) => {
    if (map && dragstart && typeof dragstart === 'function') {
      google.maps.event.addListener(map, 'dragstart', () => dragstart());
    }
  }

  _updateDragendEvent = (map, dragend) => {
    if (map && dragend && typeof dragend === 'function') {
      google.maps.event.addListener(map, 'dragend', () => dragend());
    }
  };

  _updateZoomChangedEvent = (map, zoomChanged) => {
    if (map && zoomChanged && typeof zoomChanged === 'function') {
      google.maps.event.addListener(map, 'zoom_changed', () => zoomChanged(map.getZoom()));
    }
  };

  _buildMap = (el) => {
    if (this.state.map || !el) return;
    if (!(this.props.center && this.props.zoom || this.props.bounds)) {
      logger.error('Warning: `Map` props must either include `center` and `zoom`, or `bounds`, on'
                 + ' the first render.');
    }
    const props = {
      bounds: this.props.bounds ? this.props.bounds : null,
      center: this.props.center ? this.props.center : null,
      zoom: this.props.zoom ? this.props.zoom : null,
    };
    const map = new google.maps.Map(el, props);
    this.setState({map});
    this._updateOptions(map, this._getOptions(this.props));
    this._updateCenter(map, this.props.center);
    this._updateZoom(map, this.props.zoom);
    this._updateBounds(map, this.props.bounds);
    this._updateBoundsChangedEvent(map, this.props.onBoundsChanged);
    this._updateCenterChangedEvent(map, this.props.onCenterChanged);
    this._updateClickEvent(map, this.props.onClick);
    this._updateDragStartEvent(map, this.props.onDragStart);
    this._updateDragendEvent(map, this.props.onDragend);
    this._updateZoomChangedEvent(map, this.props.onZoomChanged);
    if (this.props.mapTypes) {
      _.each(this.props.mapTypes, type => {
        if (MapTypeRules[type]) {
          map.mapTypes.set(type, new google.maps.StyledMapType(...MapTypeRules[type]));
        }
      });
      map.setMapTypeId(this.props.mapTypes[0]);
    }
  };

  render() {
    return (
      <div className={styles.root}>
        <div className={styles.container} ref={this._buildMap} />
        <ReactResizeDetector handleWidth handleHeight onResize={this._onResize} />
        {this.props.children}
      </div>
    );
  }
}

export default Map;



// WEBPACK FOOTER //
// ./src/common/components/BetterMap/Map.js